/**
 * 表格数据转换工具
 * 将接口返回的数据格式转换为 TableAnalytics 需要的格式
 */
import { ITableColumnInfo, ITableAnalyticsData, ColumnDataType } from '@/pages/datalab/components/CustomComponents/AliTableAnalytics/types';

// TableAnalytics 需要的表格数据格式
export interface ITableData {
  columnName: ITableColumnInfo[];
  data: ITableAnalyticsData[];
  count?: number;
}

// 接口返回的列信息格式
export interface IApiColumnInfo {
  name: string;
  name_en: string;
  origin_name: string;
  can_sort: boolean;
  type: number; // 0 指标列, 1 维度列, 2 观测天数列
  column_data_type: ColumnDataType;
}

// 接口返回的数据行格式
export interface IApiTableRow {
  [key: string]: {
    value: any;
    value_type?: string;
    value_en?: string;
    value_cn?: string;
  };
}

// 接口返回的完整数据格式
export interface IApiTableResponse {
  column_name: IApiColumnInfo[];
  table_data: IApiTableRow[];
  count?: number;
  actual_count?: number;
  final_sql?: string;
}

/**
 * 将接口返回的列信息转换为 ITableColumnInfo
 */
function convertColumnInfo(column: IApiColumnInfo): ITableColumnInfo {
  return {
    name: column.origin_name,
    name_cn: column.name,
    name_en: column.name_en || column.name,
    origin_name: column.origin_name,
    can_sort: column.can_sort,
    type: column.type,
    column_date_type: column.column_data_type,
    accuracy: 0,
    unit: null,
    table_code: 't0',
  };
}

/**
 * 将接口返回的数据转换为 TableAnalytics 需要的格式
 */
export function convertApiDataToTableFormat(
  apiData: IApiTableResponse,
  isTranspose: boolean,
  limit?: number,
): ITableData {
  if (!apiData?.column_name || !apiData?.table_data) {
    return {
      columnName: [],
      data: [],
    };
  }
  // 原始表格展示
  const columnName = apiData?.column_name?.map((column) => convertColumnInfo(column)) || [];
  const data = apiData?.table_data || [];
  const result: ITableData = {
    columnName,
    data,
    count: apiData.count ?? apiData.actual_count ?? 0,
  };
  // 转置后的表格展示
  if (isTranspose) {
    // 转置后的列名(维度列)
    const dimensionColumnName = columnName?.filter((column) => column?.type === 1);
    // 指标列
    const uniqueMetricNameList = data?.reduce<{ value: string; value_cn?: string; value_en?: string; }[]>(
      (acc, item) => {
        const metricName = item?.metric_name as { value: string; value_cn?: string; value_en?: string; };
        const value = metricName?.value;
        if (typeof value !== 'string' || !value) {
          return acc;
        }
        const hasExists = acc.some((metric) => metric.value === value);
        if (!hasExists) {
          acc.push({
            value,
            value_cn: metricName?.value_cn,
            value_en: metricName?.value_en,
          });
        }
        return acc;
      },
      [],
    );
    const metricColumnName: ITableColumnInfo[] = uniqueMetricNameList.map((metricName) => {
      const metricNameCn = metricName.value_cn || metricName.value;
      const metricNameEn = metricName.value_en || metricName.value;
      return {
        name: metricName.value,
        name_cn: metricNameCn,
        name_en: metricNameEn,
        origin_name: metricName.value,
        can_sort: true,
        type: 0,
        column_date_type: 'number' as ColumnDataType,
        accuracy: 0,
        unit: null,
        table_code: 't0',
      };
    });
    // 观测天数列
    const observationDaysColumnName: ITableColumnInfo = {
      name: 'observation_days',
      name_cn: '观测天数',
      name_en: 'Observation Days',
      origin_name: 'observation_days',
      can_sort: true,
      type: 2,
      column_date_type: 'number' as ColumnDataType,
      accuracy: 0,
      unit: null,
      table_code: 't0',
    };
    const transposeColumnName = [
      ...dimensionColumnName,
      observationDaysColumnName,
      ...metricColumnName,
    ];
    // 转置数据
    const dayKeyList = Object.keys(data?.[0] || {}).filter((key) =>
      /^day\d+$/i.test(key),
    );
    const transposeData = data?.reduce<ITableAnalyticsData[]>(
      (acc, item) => {
        const metricName = item?.metric_name as { value?: string; } | undefined;
        const metricColumnKey = metricName?.value;
        if (!metricColumnKey) {
          return acc;
        }
        dayKeyList.forEach((dayKey) => {
          const dayOrder = dayKey.replace(/^day/i, '');
          const dimensionKey = dimensionColumnName
            .map((column) => item?.[column.origin_name]?.value ?? '')
            .join('__');
          const rowKey = `${dimensionKey}__${dayOrder}`;
          const existsRow = acc.find(
            (row) => row.__transpose_row_key?.value === rowKey,
          );
          if (!existsRow) {
            const nextRow: ITableAnalyticsData = {
              __transpose_row_key: { value: rowKey },
            };
            dimensionColumnName.forEach((column) => {
              nextRow[column.origin_name] = item?.[column.origin_name] || {
                value: null,
              };
            });
            nextRow.observation_days = {
              value: dayOrder,
              value_cn: `第${dayOrder}天`,
              value_en: `Day ${dayOrder}`,
              value_type: 'number',
            };
            metricColumnName.forEach((column) => {
              nextRow[column.origin_name] = {
                value: null,
              };
            });
            acc.push(nextRow);
          }
          const targetRow = acc.find(
            (row) => row.__transpose_row_key?.value === rowKey,
          );
          if (targetRow) {
            targetRow[metricColumnKey] = item?.[dayKey] || {
              value: null,
            };
          }
        });
        return acc;
      },
      [],
    ).map((rowItem) => {
      const { __transpose_row_key: transposeRowKey, ...restRow } = rowItem;
      void transposeRowKey;
      return restRow as ITableAnalyticsData;
    });
    return {
      columnName: transposeColumnName,
      data: typeof limit === 'number' ? transposeData?.slice(0, limit) : transposeData,
      count: transposeData?.length,
    };
  }
  return result;
}
